import React, { useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useConfigContext, useLocalStorageState } from '../../hooks'
import LearningBadge from '../../components/LearningBadge'
import Header from '../../components/Header'
import PageSwitcher from '../../components/PageSwitcher'
import Shielded from '../../assets/Shielded.svg'
import Overlay from '../../components/Overlay'
import BlueBadge from '../../components/BlueBadge'
import { BadgesCtx } from '../../BadgesCtx'
import useImage from '../../hooks/useImage'
import { hardcodedDataOnPage } from '../../constants/assets/data'
import { getParam } from '../../helpers/parameters'

const Wrapper = styled.div`
  background: #ffffff;
  height: 100%;
  width: 100%;
  margin: 0 auto;
  overflow-y: auto;
`

const Body = styled.div`
  margin-bottom: 100px;

  @media (max-width: 767px) {
    margin-top: 0px;
    margin-bottom: 0px;
    padding-bottom: 137px;
  }
`

const Info = styled.p`
  color: #373737;
  font-size: 14px;
  padding-bottom: 56px;
  width: 80%;
  margin: 0 auto;

  display: flex;
  flex-direction: column;

  h1 {
    font-weight: 700;
    font-size: 22px;
    color: #313334;
    margin-top: 16px;
  }

  h2 {
    font-size: 14px;
    margin: 0 auto;
    color: #373737;
    font-weight: normal;
  }

  @media (min-width: 300px) and (max-width: 767px) {
    padding-bottom: 30px;
  }
`

const BadgesWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  flex-wrap: wrap;
`

const MainMenu = () => {
  const badgeCtx = useContext(BadgesCtx)
  const { displayBadge, setDisplayBadge } = badgeCtx
  const { categories, extraBadgesProgress } = useContext(BadgesCtx)
  const assetsUrl = getParam('assetsUrl')
  const { customerConfig } = useConfigContext()
  const welcomeBadge = extraBadgesProgress.find((i) => i.slug === 'welcome-to-the-family')
  const protectBadge = extraBadgesProgress.find((i) => i.slug === 'shielded')
  const pagesData = useMemo(
    () => hardcodedDataOnPage[customerConfig.customer].pagesData.mainMenu,
    [],
  )
  const { image } = useImage(
    hardcodedDataOnPage[customerConfig.customer].badges?.find((i) => i.type === 'welcome')
      ?.icon_name,
  )

  const completeWelcomeBadge = () => {
    setDisplayBadge(false)
    if (welcomeBadge) {
      badgeCtx.completeExtraBadge(welcomeBadge.name)
    }
  }

  const displayCategories = () => (
    <BadgesWrapper className="badges">
      {categories
        && categories.map((category) => (
          <LearningBadge
            key={category.id}
            title={category.name}
            icon={`${assetsUrl}${category.icon}`}
            progress={badgeCtx.getCategoryById(category.id)?.progress || 0}
            onClick={() => ({ pathname: `/badges/${category.id}` })}
          />
        ))}
      <LearningBadge
        key="protect"
        title="Device Protection Plan"
        icon={Shielded}
        progress={protectBadge.isDone ? 100 : 0}
        onClick={() => ({ pathname: '/protect' })}
      />
    </BadgesWrapper>
  )

  return (
    <>
      {displayBadge
        && createPortal(
          <Overlay>
            <BlueBadge
              title={welcomeBadge?.name}
              linkTitle="Access section"
              onClose={completeWelcomeBadge}
              text={welcomeBadge?.description}
              icon={image}
            />
          </Overlay>,
          document.body,
        )}
      <Wrapper
        className="main-menu-page"
      >
        <Header pageName={'Main Menu'} inverted={true} />
        <PageSwitcher
          navLeft="/learning-curve"
          navRight="/explore-more"
          pageTitle="Main menu"
          pageNumber={1}
        />
        <Body as='main' id="main-content">
          <Info>
            <h1>{pagesData.title}</h1>
            <h2>{pagesData.description}</h2>
          </Info>
          {displayCategories()}
        </Body>
      </Wrapper>
    </>
  )
}

export default MainMenu
